import React from 'react';
import { Box, Typography, Skeleton, Tooltip, useTheme, alpha } from '@mui/material';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { AddressType } from '../../../store/reducers/SavedAddress/Type';

export interface SavedAddressChipsProps {
  title: string;
  type: 'shipper' | 'consignee';
  addresses?: AddressType[];
  loading?: boolean;
  error?: any;
  currentAddress?: any;
  currentPincode?: string;
  selectedAddressId?: string | null;
  onSelectAddress: (address: AddressType) => void;
}

export const SavedAddressChips: React.FC<SavedAddressChipsProps> = ({
  title,
  type,
  addresses = [],
  loading = false,
  error = null,
  currentAddress,
  currentPincode,
  selectedAddressId,
  onSelectAddress,
}) => {
  const theme = useTheme();

  const filteredAddresses = React.useMemo(() => {
    if (!Array.isArray(addresses)) return [];
    return addresses.filter(addr => !!addr && (addr.type ? addr.type === type : true));
  }, [addresses, type]);

  return (
    <Box sx={{ mt: 1.5, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <BookmarkBorderRoundedIcon
          sx={{
            fontSize: '1rem',
            color: theme.palette.primary.main,
            opacity: 0.85,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: '0.775rem',
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {title}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Skeleton variant="rectangular" width={130} height={42} sx={{ borderRadius: '10px' }} animation="wave" />
          <Skeleton variant="rectangular" width={140} height={42} sx={{ borderRadius: '10px' }} animation="wave" />
          <Skeleton variant="rectangular" width={120} height={42} sx={{ borderRadius: '10px' }} animation="wave" />
        </Box>
      ) : error && (!filteredAddresses || filteredAddresses.length === 0) ? (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            display: 'block',
            fontSize: '0.75rem',
          }}
        >
          Unable to load saved addresses
        </Typography>
      ) : filteredAddresses.length === 0 ? (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            display: 'block',
            fontSize: '0.75rem',
          }}
        >
          {type === 'shipper' ? 'No saved pickup addresses available' : 'No saved destination addresses available'}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            maxHeight: '135px',
            overflowY: 'auto',
            pr: 0.5,
            py: 0.5,
            '::-webkit-scrollbar': {
              width: '4px',
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.2),
              borderRadius: '4px',
            },
          }}
        >
          {filteredAddresses.map((addr, index) => {
            const primaryText = (addr.label || addr.companyName || addr.name || 'Saved Address').trim();

            const secondaryParts = [addr.city, addr.pincode].filter(
              val => typeof val === 'string' && val.trim().length > 0,
            );
            const secondaryText = secondaryParts.join(' • ');

            const isSelected =
              Boolean(selectedAddressId && addr._id && selectedAddressId === addr._id) ||
              Boolean(
                currentPincode &&
                  addr.pincode &&
                  currentPincode.trim() === addr.pincode.trim() &&
                  ((addr.name && currentAddress?.name === addr.name) ||
                    (addr.line1 && currentAddress?.line1 === addr.line1) ||
                    (addr.label && currentAddress?.label === addr.label)),
              );

            const tooltipAddress = [
              addr.name,
              addr.companyName,
              addr.line1,
              addr.line2,
              addr.city,
              addr.state,
              addr.country,
              addr.pincode,
            ]
              .filter(val => typeof val === 'string' && val.trim().length > 0)
              .join(', ');

            return (
              <Tooltip key={addr._id || index} title={tooltipAddress || primaryText} arrow placement="top">
                <Box
                  onClick={() => onSelectAddress(addr)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: isSelected ? '1.5px solid' : '1px solid',
                    borderColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.9),
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : '#f9fafb',
                    boxShadow: isSelected ? `0 2px 6px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                    transition: 'all 0.18s ease-in-out',
                    maxWidth: '100%',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-1px)',
                      boxShadow: '0 3px 8px rgba(0,0,0,0.06)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                  }}
                >
                  {isSelected ? (
                    <CheckCircleRoundedIcon
                      sx={{
                        fontSize: '16px',
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.text.secondary, 0.4),
                        flexShrink: 0,
                      }}
                    />
                  )}

                  <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: isSelected ? 700 : 600,
                        fontSize: '0.8rem',
                        color: isSelected ? theme.palette.primary.main : 'text.primary',
                        lineHeight: 1.2,
                        maxWidth: { xs: '180px', sm: '210px' },
                      }}
                    >
                      {primaryText}
                    </Typography>
                    {secondaryText ? (
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          fontSize: '0.7rem',
                          color: isSelected ? alpha(theme.palette.primary.main, 0.85) : 'text.secondary',
                          display: 'block',
                          lineHeight: 1.2,
                          mt: 0.25,
                        }}
                      >
                        {secondaryText}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SavedAddressChips;
